import { ComponentRef, useMemo, useRef } from 'react';

import { Select } from '~/modules/elements/akui/select';
import { NavButton } from '~/modules/elements/nav-controls';
import styles from '~/modules/game-engine/drawing/styles';
import { useRegister } from '~/modules/hooks/use-keyboard-nav';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { assignProfileToPlayer } from '~/modules/user-profiles/assign';
import { usePlayerProfile } from '~/modules/user-profiles/hooks';
import UserProfiles, { ProfileWriteError, UserProfile } from '~/modules/user-profiles/profiles';
import { usePlayerInput } from '~/routes/select-input/hooks/use-player-input';
import {
  canPickInput,
  currentInputOption,
  decodeInputOption,
  inputOptions,
  optionsForSlot,
} from '~/routes/select-input/input-options';
import { InputSourceList } from '~/routes/select-input/input-sources/interfaces';
import { RemoteMicrophoneInputSource } from '~/routes/select-input/input-sources/remote';
import PlayerSlotCard from '~/routes/select-input/player-slot-card';
import { currentSingerOption, NEW_SINGER, resolveNewSinger, singerOptions } from '~/routes/select-input/singer-options';
import { SlotState } from '~/routes/select-input/slot-state';

interface Props {
  slot: SlotState;
  /** Every slot, this one included - what makes a microphone another slot already holds unpickable. */
  slots: SlotState[];
  sources: Record<string, InputSourceList>;
  profiles: UserProfile[];
  // Which slot each profile is already singing on - see `profileHolders`.
  heldBy: Partial<Record<string, PlayerNumber>>;
  focused: boolean;
  /** True while a song is in progress, when the roster cannot take anybody new - see `SlotState`. */
  rosterLocked: boolean;
  /** Two rows of four rather than one: see `PlayerSlotCard`. */
  compact?: boolean;
  onAdd: () => void;
  onRemove: () => void;
  /** Opens the profile form for this slot - owned by the screen, which is where the modal lives. */
  onNewSinger: () => void;
}

/**
 * A slot card with the setup screen's own actions on it. `usePlayerInput` is what keeps a slot
 * pointed at something real when the phone it was on disappears or its device is unplugged.
 */
export default function MicSlot({
  slot,
  slots,
  sources,
  profiles,
  heldBy,
  focused,
  rosterLocked,
  compact = false,
  onAdd,
  onRemove,
  onNewSinger,
}: Props) {
  const [selectedInput] = usePlayerInput(slot.number, sources);
  const register = useRegister();
  const pickerRef = useRef<ComponentRef<typeof Select>>(null);
  const singerRef = useRef<ComponentRef<typeof Select>>(null);
  const player = `Player ${slot.number + 1}`;
  // What cards this slot's rows together on the phone, which groups by control TYPE otherwise - so
  // two slots' pickers card up as one list and each ✕ falls outside, between them. The label carries
  // the slot's own state, because an emptied card has nothing else on it to say which of the slots
  // it is.
  const group = {
    id: `player-${slot.number}`,
    label: slot.hasInput ? player : `${player} · empty`,
    colour: styles.colors.players[slot.number]?.text,
    player: slot.number,
  };
  // Reached from the pause menu mid-song, Add on a slot with nobody behind it used to be a button
  // that did nothing at all: `addPlayer` refuses while the song is playing, because the scoring
  // state and the lane geometry are both fixed when the song starts.
  const canAdd = slot.hasPlayer || !rosterLocked;
  // The same lock the other way: `removePlayer` refuses mid-song, so ✕ on a slot above the minimum is
  // off rather than a press the phone would report as done.
  const canRemove = !rosterLocked || slot.number < PlayersManager.getMinPlayerNumber();

  const options = useMemo(() => inputOptions(sources, slots), [sources, slots]);
  const chosen = currentInputOption(options, selectedInput);
  // One list, rendered by the television and by the phone - so "Mia's phone, on Player 2" reads the
  // same on both rather than the two agreeing by coincidence.
  const choices = useMemo(() => optionsForSlot(options, slot.number, chosen), [options, slot.number, chosen]);

  const choose = (value: string) => {
    // Checked rather than trusted: the on-screen list will not offer a held input and neither will a
    // phone on this build, but a phone on an older one has never heard of `disabled` and sends the
    // value anyway - and `changeInput` would take it, which is the bug.
    if (!canPickInput(options, slot.number, value, chosen)) return;

    const option = decodeInputOption(options, value);
    if (!option) return;

    // An empty slot can take a microphone straight from the phone's list; the television's own way
    // in is the Add button.
    const target = PlayersManager.getPlayer(slot.number) ?? (canAdd ? PlayersManager.addPlayer(slot.number) : null);
    target?.changeInput(option.source, option.channel, option.deviceId);
  };

  const profile = usePlayerProfile(slot.number);
  // A phone puts its own name on the slot and that beats any assignment (see `assign.ts`), so the
  // picker would not land - it says who is singing instead of offering a choice that cannot stick.
  const fromPhone = selectedInput?.source === RemoteMicrophoneInputSource.inputName;
  // The phone locks a singer another slot is singing as; the television's own picker stays free, as it
  // always was, so whoever holds the remote can still move a singer between slots by hand.
  const singerChoices = useMemo(() => singerOptions(profiles), [profiles]);
  const phoneSingerChoices = useMemo(
    () => singerOptions(profiles, heldBy, slot.number),
    [profiles, heldBy, slot.number],
  );
  const isFreeFor = (profile: UserProfile) => (heldBy[profile.id] ?? slot.number) === slot.number;
  const chosenSinger = currentSingerOption(profile);

  const chooseSinger = (value: string, fromRemote = false) => {
    if (value === NEW_SINGER) {
      onNewSinger();
      return;
    }

    // Nothing else is offered, so anything that is not a profile is ignored rather than acted on -
    // a phone can send a value for any control, and a stale id from a profile deleted since the
    // list was mirrored must not empty the slot. Handing a slot back is the Profiles screen's job,
    // which is why there is no "nobody" to pick here - see `singer-options.ts`.
    const picked = profiles.find(({ id }) => id === value);
    if (picked && (!fromRemote || isFreeFor(picked))) assignProfileToPlayer(slot.number, picked);
  };

  const nameNewSinger = (value: string) => {
    const outcome = resolveNewSinger(value, profiles, heldBy, slot.number);
    if (outcome.kind === 'assign') assignProfileToPlayer(slot.number, outcome.profile);
    if (outcome.kind !== 'create') return;

    try {
      assignProfileToPlayer(slot.number, UserProfiles.create(outcome.name));
    } catch (error) {
      if (!(error instanceof ProfileWriteError)) throw error;
      console.warn('Could not create a profile for a new singer', error);
    }
  };

  // Registered ahead of the input picker, because the phone's rows follow the order `register` is
  // CALLED in and this one is drawn above it on the card. Only on a slot that holds a microphone: an
  // empty slot sits out the song, so there is nobody to name, and a registered stop with nothing on
  // screen is a place the keyboard can go and the pointer cannot.
  const singerProps = slot.hasInput
    ? register(`player-${slot.number}-singer`, () => singerRef.current?.element?.focus(), `${player} sings as`, false, {
        disabled: fromPhone,
        control: {
          type: 'select' as const,
          group,
          label: `${player} sings as`,
          value: chosenSinger,
          // The on-screen list entire, `+ New singer…` included: the form it opens publishes its own
          // layout and offers the phone's camera, so a phone that picks it can finish the job.
          options: phoneSingerChoices,
        },
        onValueChange: (value) => chooseSinger(value, true),
      })
    : undefined;

  // Keeps the name the cycling "Change" button had, so the e2e ids and the phone's mirrored row are
  // both where they were - only what the control DOES changed. Focusing the field is what opens the
  // list, so the keyboard has to focus it the way `leaderboard-country` does.
  const pickerProps = register(
    `player-${slot.number}-input`,
    () => pickerRef.current?.element?.focus(),
    `${player} input`,
    false,
    {
      control: {
        type: 'select' as const,
        group,
        label: `${player} input`,
        value: chosen,
        options: choices,
      },
      onValueChange: choose,
    },
  );

  if (slot.hasInput && !fromPhone) {
    register(`player-${slot.number}-new-singer`, () => undefined, `${player} new singer`, false, {
      remoteOnly: true,
      control: { type: 'text' as const, group, label: `New singer for ${player}`, value: '' },
      onValueChange: nameNewSinger,
    });
  }

  const singer = !singerProps ? undefined : fromPhone ? (
    // `register(disabled)` hands back no props at all, test id included, so the name is written out
    // rather than put in a field nothing could open. The id is the registered name verbatim, which
    // is what makes hard-coding it safe here - see `use-keyboard-nav`.
    <div
      data-test={`player-${slot.number}-singer`}
      data-test-value={slot.title}
      className="ph-no-capture flex h-12 items-center rounded-xl border border-white/10 bg-black/30 px-3.5 text-[1.0625rem] text-white/55">
      <span className="truncate">{slot.title} · from the phone</span>
    </div>
  ) : (
    <Select
      {...singerProps}
      className="min-w-0 [&_input::placeholder]:text-white/45"
      label=""
      aria-label={`${player} sings as`}
      // The slot still sings and still scores with nobody on it - under "Player 2", which is what
      // `getName()` falls back to. Naming that is the whole point: "Nobody" denied a singer was
      // there while the round was already being recorded against them.
      placeholder={player}
      data-test-value={profile?.name ?? player}
      value={chosenSinger}
      onChange={(value) => chooseSinger(value)}
      options={singerChoices}
      ref={singerRef}
    />
  );

  return (
    <PlayerSlotCard slot={slot} focused={focused} singer={singer} compact={compact}>
      {slot.hasInput ? (
        <>
          {/* Every input at once instead of a cycle. `b8832c46c` removed the old `player-N-source`
              so that one press walked sources and devices together; this shows that same combined
              list rather than stepping through it, which on several slots and three devices was a
              dozen presses or more to reach the last one. */}
          <Select
            {...pickerProps}
            className="min-w-0 flex-1"
            label=""
            aria-label={`${player} input`}
            placeholder="Pick a microphone"
            value={chosen}
            onChange={choose}
            options={choices}
            ref={pickerRef}
          />
          <NavButton
            name={`player-${slot.number}-remove`}
            size="small"
            className="h-12 w-14 px-0 text-[1.0625rem]"
            group={group}
            variant="destructive"
            remoteLabel={`Clear ${player}`}
            disabled={!canRemove}
            onClick={onRemove}>
            ✕
          </NavButton>
        </>
      ) : (
        <NavButton
          name={`player-${slot.number}-add`}
          // `register` returns no props at all for a disabled control, test id included, and the
          // slot still has to be identifiable while it says why it cannot be filled. Safe only
          // because it is the registered name verbatim - see `use-keyboard-nav`.
          data-test={`player-${slot.number}-add`}
          size="small"
          className="h-12 flex-1 text-[1.0625rem]"
          group={group}
          remoteLabel={canAdd ? `Add a mic for ${player}` : `${player} joins after the song`}
          disabled={!canAdd}
          onClick={onAdd}>
          {canAdd ? 'Add a microphone' : 'Joins after the song'}
        </NavButton>
      )}
    </PlayerSlotCard>
  );
}
